import { CtxMapV2 } from 'components/mapV2/contexts/CtxMapV2';
import { DetailQuery_moment } from 'graphql/query/__generated__/DetailQuery';
import { useZoomToMarker } from 'hooks/map/useZoomToMarker';
import { useAnalytics } from 'libs/utils/site/analytics';
import { useContext } from 'react';
import { CtxModal } from 'session/ctxModal';
import { CtxView } from 'session/ctxView';

export const useActions = () => {
    const ctxView = useContext(CtxView);
    const ctxMapV2 = useContext(CtxMapV2);
    const ctxModal = useContext(CtxModal);
    const analytics = useAnalytics();
    const isWide = ctxMapV2.isWide;
    const { zoomToMarkers } = useZoomToMarker({ speed: 2, offsetY: isWide ? 0 : -200 });

    // Return if the click was handled
    function handleMapClick() {
        if (!ctxMapV2.isWide) {
            return null;
        }
        const currentMarkerId = ctxModal.detailModal?.markerId;
        if (currentMarkerId) {
            ctxModal.setDetailModal(null);
            return true;
        }
        const currentListIds = ctxModal.detailListModal?.storyIds;
        if (currentListIds) {
            ctxModal.setDetailListModal(null);
            return true;
        }
        return false;
    }

    function handleMarkersClick(markerIds: string[]) {
        const markers = ctxView.allMarkers.filter((e) => markerIds.includes(e.worldMapMarkerId));

        if (!markers.length) {
            return null;
        }

        if (markers.length === 1) {
            return handleMarkerClick(markers[0].worldMapMarkerId);
        }

        const storyIds = markers.flatMap((e) => e.stories?.map((s) => s.storyId) ?? []);
        const stories = ctxView.allDetails.filter((e) => storyIds.includes(e.storyId));

        if (!stories.length) {
            return null;
        }

        analytics.track('cluster.clicked', {
            action: 'detail.opened'
        });

        ctxModal.setDetailListModal({
            storyIds,
            sort: 'title',
            show: 'all'
        });
        if (isWide) {
            ctxMapV2.pcModalState.open();
        } else {
            ctxMapV2.mobileModalState.setSize('large');
        }
    }

    function handleMarkerClick(markerId: string) {
        const marker = ctxView.allMarkers.find((e) => e.worldMapMarkerId === markerId);

        if (!marker) {
            return null;
        }
        const currentMarkerId = ctxModal.detailModal?.markerId;

        const storyId = marker.stories?.[0]?.storyId;
        const story = ctxView.allDetails.find((e) => e.storyId === storyId);

        if (!story) {
            return null;
        }

        if (currentMarkerId === marker.worldMapMarkerId) {
            if (isWide) {
                return;
            } else {
                ctxMapV2.mobileModalState.setSize('large');
                return;
            }
        }

        ctxModal.setDetailModal({
            id: story.storyId,
            detail: story,
            markerId: marker.worldMapMarkerId
        });
        if (!ctxMapV2.features.isListEnabled && !ctxMapV2.isWide) {
            ctxMapV2.mobileModalState.setSize('large');
        }
        ctxModal.setDetailListModal(null);
        analytics.track('marker.clicked', {
            action: 'detail.opened'
        });
    }

    function openStoryDetail(story: DetailQuery_moment) {
        if (!story) {
            return;
        }
        ctxModal.setDetailModal({
            id: story.storyId,
            markerId: story.connectedMarkers?.[0]?.worldMapMarkerId,
            detail: story
        });
        zoomToMarkers(story.connectedMarkers);
    }

    function closeModal() {
        ctxModal.setDetailModal(null);
        if (isWide) {
            ctxMapV2.pcModalState.close();
        } else {
            ctxMapV2.mobileModalState.setSize('small');
        }
    }

    function returnToList() {
        ctxModal.setDetailModal(null);
    }

    return {
        handleMarkerClick,
        handleMarkersClick,
        handleMapClick,
        openStoryDetail,
        closeModal,
        returnToList
    };
};
